import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import {
  MessageCircle, X, Send, Car, Search, BookOpen, Shield,
  Sparkles, ChevronRight, MapPin, Calendar, Gauge, Fuel,
  Loader2, Bot, Minimize2, Maximize2, RotateCcw
} from 'lucide-react';
import { useTranslation } from '../LanguageContext';

const ChatWidget = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [showPulse, setShowPulse] = useState(true);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, isMinimized]);

  // Hide pulse after first open
  useEffect(() => {
    if (isOpen) setShowPulse(false);
  }, [isOpen]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId
      });

      const botMsg = {
        role: 'assistant',
        content: response.data.response,
        action: response.data.action,
        data: response.data.data,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, botMsg]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('chat.error'),
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickAction = (text) => {
    sendMessage(text);
  };

  const resetChat = () => {
    setMessages([]);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'EUR',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(price);
  };

  const quickActions = [
    { icon: Car, label: t('chat.findCars'), prompt: t('chat.findCarsPrompt') },
    { icon: Shield, label: t('chat.vinCheckBtn'), prompt: t('chat.vinPrompt') },
    { icon: BookOpen, label: t('chat.carWikiBtn'), prompt: t('chat.wikiPrompt') },
    { icon: Sparkles, label: t('chat.recommend'), prompt: t('chat.recommendPrompt') },
  ];

  // Render mini car cards for search results
  const renderCarResults = (cars) => {
    if (!cars || cars.length === 0) return null;
    return (
      <div className="grid grid-cols-2 gap-1.5 mt-2">
        {cars.slice(0, 4).map(car => (
          <Link
            key={car.id}
            to={`/cars/${car.id}`}
            onClick={() => setIsOpen(false)}
            className="block rounded-lg bg-white/5 border border-white/5 hover:border-cyan-500/30 overflow-hidden transition-all group"
          >
            <div className="aspect-[16/9] overflow-hidden">
              <img
                src={car.images?.[0] || 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400'}
                alt={car.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="p-1.5">
              <div className="text-[10px] font-medium text-white truncate leading-tight">{car.title}</div>
              <div className="text-[10px] font-bold text-cyan-500">{formatPrice(car.price)}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] text-gray-500 flex items-center gap-0.5">
                  <Calendar className="w-2.5 h-2.5" />{car.year}
                </span>
                <span className="text-[9px] text-gray-500 flex items-center gap-0.5">
                  <Gauge className="w-2.5 h-2.5" />{(car.mileage / 1000).toFixed(0)}K
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    );
  };

  // Render VIN result
  const renderVinResult = (vinData) => {
    if (!vinData || !vinData.valid) return null;
    return (
      <div className="mt-2 p-2.5 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
        <div className="text-[10px] font-semibold text-cyan-500 mb-1.5 uppercase tracking-wider">{t('chat.vinReport')}</div>
        <div className="grid grid-cols-2 gap-1.5">
          {[
            { label: 'Brand', value: vinData.brand },
            { label: 'Year', value: vinData.year },
            { label: 'Body', value: vinData.body_type },
            { label: 'Engine', value: vinData.engine },
            { label: 'Accidents', value: vinData.accidents },
            { label: 'Owners', value: vinData.owners },
            { label: 'Service Records', value: vinData.service_records },
            { label: 'Est. Value', value: vinData.estimated_value ? formatPrice(vinData.estimated_value) : 'N/A' },
          ].map((item, i) => (
            <div key={i} className="text-[10px]">
              <span className="text-gray-500">{item.label}:</span>{' '}
              <span className="text-white font-medium">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render wiki results
  const renderWikiResults = (wikiData) => {
    if (!wikiData) return null;
    return (
      <div className="mt-2 space-y-1">
        {wikiData.brands?.slice(0, 3).map(brand => (
          <Link
            key={brand.id}
            to={`/wiki/${brand.name}`}
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-cyan-500/10 border border-white/5 hover:border-cyan-500/20 transition-all"
          >
            <BookOpen className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] font-medium text-white">{brand.name}</div>
              <div className="text-[9px] text-gray-500">{brand.country} · Founded {brand.founded}</div>
            </div>
            <ChevronRight className="w-3 h-3 text-gray-600 ml-auto shrink-0" />
          </Link>
        ))}
        {wikiData.models?.slice(0, 3).map(model => (
          <Link
            key={model.id}
            to={`/wiki/model/${model.id}`}
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-cyan-500/10 border border-white/5 hover:border-cyan-500/20 transition-all"
          >
            <Car className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] font-medium text-white">{model.brand} {model.name}</div>
              <div className="text-[9px] text-gray-500">{model.body_types?.join(', ')}</div>
            </div>
            <ChevronRight className="w-3 h-3 text-gray-600 ml-auto shrink-0" />
          </Link>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => { setIsOpen(!isOpen); setIsMinimized(false); }}
        className={`fixed bottom-5 right-5 z-50 w-13 h-13 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 ${
          isOpen
            ? 'bg-white/10 backdrop-blur-xl border border-white/20 rotate-0'
            : 'bg-gradient-to-br from-cyan-500 to-cyan-600 shadow-neon hover:shadow-neon-lg hover:scale-105'
        }`}
        style={{ width: 52, height: 52 }}
        data-testid="chat-toggle"
      >
        {isOpen ? (
          <X className="w-5 h-5 text-white" />
        ) : (
          <>
            <MessageCircle className="w-5 h-5 text-black" />
            {showPulse && (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
            )}
            {showPulse && (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full"></span>
            )}
          </>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div
          className={`fixed z-50 transition-all duration-300 ${
            isMinimized
              ? 'bottom-20 right-5 w-72 h-12'
              : 'bottom-20 right-5 w-[380px] h-[520px] max-h-[calc(100vh-120px)]'
          }`}
          data-testid="chat-panel"
        >
          <div className="w-full h-full rounded-2xl bg-[#0a0a10]/95 backdrop-blur-2xl border border-white/10 shadow-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 shrink-0 bg-gradient-to-r from-cyan-500/5 to-transparent">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5 text-black" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-white">{t('chat.title')}</div>
                  <div className="text-[9px] text-cyan-500 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                    {t('chat.online')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={resetChat} className="p-1.5 text-gray-500 hover:text-white rounded-md hover:bg-white/5 transition-colors" title={t('chat.reset')}>
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setIsMinimized(!isMinimized)} className="p-1.5 text-gray-500 hover:text-white rounded-md hover:bg-white/5 transition-colors">
                  {isMinimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 scrollbar-thin">
                  {messages.length === 0 && (
                    <div className="text-center py-6">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 flex items-center justify-center mx-auto mb-3 border border-cyan-500/20">
                        <Sparkles className="w-6 h-6 text-cyan-500" />
                      </div>
                      <h3 className="text-sm font-semibold text-white mb-1">{t('chat.welcome')}</h3>
                      <p className="text-[11px] text-gray-500 max-w-[240px] mx-auto leading-relaxed">
                        {t('chat.welcomeDesc')}
                      </p>
                    </div>
                  )}

                  {messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] ${msg.role === 'user' ? '' : ''}`}>
                        {msg.role === 'assistant' && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <div className="w-4 h-4 rounded-full bg-cyan-500/20 flex items-center justify-center">
                              <Bot className="w-2.5 h-2.5 text-cyan-500" />
                            </div>
                            <span className="text-[9px] text-gray-600">{t('chat.title')}</span>
                          </div>
                        )}
                        <div className={`rounded-xl px-3 py-2 text-[12px] leading-relaxed ${
                          msg.role === 'user'
                            ? 'bg-cyan-500 text-black rounded-br-sm'
                            : 'bg-white/5 text-gray-200 border border-white/5 rounded-bl-sm'
                        }`}>
                          {msg.content}
                        </div>

                        {/* Render data results */}
                        {msg.data?.type === 'cars' && renderCarResults(msg.data.cars)}
                        {msg.data?.type === 'vin' && renderVinResult(msg.data.vin)}
                        {msg.data?.type === 'wiki' && renderWikiResults(msg.data.wiki)}
                      </div>
                    </div>
                  ))}

                  {/* Loading indicator */}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/5">
                        <div className="flex gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                        <span className="text-[10px] text-gray-500">{t('chat.thinking')}</span>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Actions */}
                {messages.length === 0 && (
                  <div className="px-3 pb-2 shrink-0">
                    <div className="grid grid-cols-2 gap-1.5">
                      {quickActions.map((action, i) => (
                        <button
                          key={i}
                          onClick={() => handleQuickAction(action.prompt)}
                          disabled={loading}
                          className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/[0.03] border border-white/5 hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-all text-left group disabled:opacity-50"
                        >
                          <action.icon className="w-3.5 h-3.5 text-cyan-500/70 group-hover:text-cyan-500 shrink-0" />
                          <span className="text-[10px] text-gray-400 group-hover:text-white">{action.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input Area */}
                <div className="px-3 pb-3 pt-1 shrink-0">
                  <form onSubmit={handleSubmit} className="relative">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={t('chat.placeholder')}
                      disabled={loading}
                      className="w-full h-9 pl-3 pr-10 text-xs bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-cyan-500/50 focus:bg-white/8 outline-none disabled:opacity-50 transition-all"
                      data-testid="chat-input"
                    />
                    <button
                      type="submit"
                      disabled={!input.trim() || loading}
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg bg-cyan-500 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-cyan-400 transition-colors"
                      data-testid="chat-send"
                    >
                      {loading ? (
                        <Loader2 className="w-3.5 h-3.5 text-black animate-spin" />
                      ) : (
                        <Send className="w-3.5 h-3.5 text-black" />
                      )}
                    </button>
                  </form>
                  <div className="text-center mt-1.5">
                    <span className="text-[9px] text-gray-700">{t('chat.powered')}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
